import {
  Accordion,
  AccordionDetails,
  AccordionSlots,
  AccordionSummary,
  Button,
  Stack,
  Typography,
} from "@mui/material";
import Fade from "@mui/material/Fade";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useState } from "react";
import {
  IBookingSettings,
  ICategory,
  ISelectedService,
  IStaffs,
} from "pages/booking/interface";
import KeyboardDoubleArrowUpIcon from "@mui/icons-material/KeyboardDoubleArrowUp";

interface IProps {
  category: ICategory;
  setStep: (agr: number) => void;
  selectedServices: Array<ISelectedService>;
  setSelectedServices: (agr: Array<ISelectedService>) => void;
  bookingSettings: IBookingSettings;
  ownerInfo: IStaffs | null;
}

const CatalogAccordion = ({
  category,
  setStep,
  selectedServices,
  setSelectedServices,
  bookingSettings,
  ownerInfo,
}: IProps) => {
  const [expanded, setExpanded] = useState(false);

  const { onlySingleStaffMember, onlyShowAnyoneOption, showServicePrice } =
    bookingSettings;

  const handleExpansion = () => {
    setExpanded((prevExpanded) => !prevExpanded);
  };

  const handlePickService =
    (
      serviceId: string,
      serviceName: string,
      bufferTime: number,
      time: number,
      servicePrice: number
    ) =>
    () => {
      const singleStaffAvailable =
        onlySingleStaffMember && !!selectedServices[0]?.staffId;

      const isOnlyShowAnyoneAvailable = onlyShowAnyoneOption && ownerInfo;

      setSelectedServices([
        ...selectedServices,
        {
          serviceId,
          serviceName,
          bufferTime,
          servicePrice,
          time,
          staffId: singleStaffAvailable
            ? selectedServices[0]?.staffId
            : isOnlyShowAnyoneAvailable
            ? ownerInfo?.staffId
            : "",
          staffName: singleStaffAvailable
            ? selectedServices[0]?.staffName
            : isOnlyShowAnyoneAvailable
            ? ownerInfo?.staffName
            : "",
        },
      ]);
      setStep(singleStaffAvailable || isOnlyShowAnyoneAvailable ? 2 : 1);
    };

  return (
    <Accordion
      expanded={expanded}
      onChange={handleExpansion}
      slots={{ transition: Fade as AccordionSlots["transition"] }}
      slotProps={{ transition: { timeout: 250 } }}
      disableGutters
      sx={{
        border: "1px solid",
        borderColor: expanded ? "#0B70E1" : "#BDBDBD",
        borderRadius: "12px !important",
        boxShadow: "none",
        "&::before": {
          display: "none",
        },
        "& .MuiAccordion-region": {
          height: expanded ? "auto" : 0,
          borderRadius: 3,
        },
        "& .MuiAccordionDetails-root": {
          display: expanded ? "block" : "none",
          backgroundColor: expanded ? "#EBF4FD" : "#fff",
          padding: "0 28px",
          borderBottomLeftRadius: 12,
          borderBottomRightRadius: 12,
        },
      }}
    >
      <AccordionSummary
        sx={{
          padding: "8px 16px",
          background: expanded ? "#0B70E1" : "transparent",
          color: expanded ? "#fff" : "inherit",
          borderTopLeftRadius: 10,
          borderTopRightRadius: 10,
          "& .MuiAccordionSummary-content": {
            margin: "0 !important",
          },
        }}
        expandIcon={
          <ExpandMoreIcon sx={{ color: expanded ? "#fff" : "inherit" }} />
        }
      >
        <Typography>{category.categoryName}</Typography>
      </AccordionSummary>
      <AccordionDetails>
        {category.services
          .filter(
            (service) =>
              service.staffs &&
              service.staffs.length > 0 &&
              // if only single member settings is true and selected services already exists, get services that contain selected staff
              (!onlySingleStaffMember ||
                !selectedServices.length ||
                service?.staffs.some(
                  (staff) => staff.staffId === selectedServices[0].staffId
                ))
          )
          .filter(
            (service) =>
              !selectedServices.some(
                (ser) => ser.serviceId === service.productId
              )
          )
          .map((service, index) => (
            <Button
              key={`${service.productId}--service-${index}`}
              variant="text"
              sx={{
                width: "100%",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                color: "#061340",
                textAlign: "left",
                padding: "12px 0",
                borderTop: index !== 0 ? "1px dashed #96C6F6" : "0",
                textTransform: "inherit",
              }}
              onClick={handlePickService(
                service.productId,
                service.productName,
                service.bufferTime,
                service.time,
                service.price
              )}
            >
              <Typography fontWeight={500}>{service.productName}</Typography>
              {showServicePrice && (
                <Stack direction="row" alignItems="center">
                  <Typography fontWeight={700}>${service.price}</Typography>
                  {service.seasonalPrice && (
                    <KeyboardDoubleArrowUpIcon
                      color="success"
                      sx={{
                        animation: "slideUp 1s infinite ease-in-out",
                        "@keyframes slideUp": {
                          "0%": {
                            transform: "translateY(10px)", // Start below
                            opacity: 0, // Fade in
                          },
                          "50%": {
                            transform: "translateY(0)", // Move to original position
                            opacity: 1, // Fully visible
                          },
                          "100%": {
                            transform: "translateY(-10px)", // Slide up
                            opacity: 0, // Fade out
                          },
                        },
                      }}
                    />
                  )}
                </Stack>
              )}
            </Button>
          ))}
      </AccordionDetails>
    </Accordion>
  );
};

export default CatalogAccordion;
